import React, { useState, useEffect, useCallback } from 'react';
import api, {
  getKbEntries,
  editKbEntry,
  archiveKbEntry,
  QaKbEntry,
} from '../../services/api';
import { relativeTime } from './helpers';

// Q&A KNOWLEDGE (AI Info "Global knowledge" sub-tab). Two parts:
//   (a) the v1 qa_knowledge_base browse — search/scope/status + keyset paging +
//       edit/archive (lifted VERBATIM from the original GlobalKb Q&A table), and
//   (b) a "Learned experience" section (GET /api/aiinfo/qa-experience) — the
//       AI's mined wrong→right resolutions, each with an Ignore/Restore toggle
//       for the underlying memory (POST /aiinfo/memory/{id}).
// Styling/loading/error-banner/confirm patterns mirror v1. isAdmin gating lives
// on the parent shell (GlobalKb).

const PAGE_SIZE = 50;

// Scope filter: '' = all. Matches the KB scope column ('general'|'customer').
const SCOPE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All scopes' },
  { value: 'general', label: 'General' },
  { value: 'customer', label: 'Customer' },
];

// Status filter. Default 'active'; 'all' omits the param so the backend returns
// both active and archived.
const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All statuses' },
];

// Scope options for the edit modal (no "all" — an entry is one or the other).
const EDIT_SCOPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'customer', label: 'Customer' },
];

// A learned-experience row from /aiinfo/qa-experience.entries[]. Typed inline
// (per the collision-avoidance rule: no additions to types/index.ts).
interface QaExperienceRow {
  id: number;
  context_text?: string | null;
  wrong_summary?: string | null;
  right_summary?: string | null;
  origin_thread_ref?: string | number | null;
  active?: boolean | null;
  created_at?: string | null;
}

const QaKnowledge: React.FC = () => {
  const [rows, setRows] = useState<QaKbEntry[]>([]);
  // One search box drives BOTH q and identityQuery. `search` is live; `query` is
  // the debounced value actually sent to the backend.
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState('');
  const [status, setStatus] = useState('active');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextBeforeId, setNextBeforeId] = useState<number | null>(null);

  const [actionError, setActionError] = useState<string | null>(null);
  const [editing, setEditing] = useState<QaKbEntry | null>(null);
  const [editQuestion, setEditQuestion] = useState('');
  const [editAnswer, setEditAnswer] = useState('');
  const [editScope, setEditScope] = useState('general');
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  // Learned experience section state.
  const [exp, setExp] = useState<QaExperienceRow[]>([]);
  const [expLoading, setExpLoading] = useState(true);
  const [expError, setExpError] = useState<string | null>(null);
  const [expSoftErrors, setExpSoftErrors] = useState<string[]>([]);
  const [expBusyId, setExpBusyId] = useState<number | null>(null);

  // Debounce the search box -> query.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const buildParams = useCallback(
    (beforeId?: number) => {
      const params: {
        limit: number;
        q?: string;
        identityQuery?: string;
        scope?: string;
        status?: string;
        before_id?: number;
      } = { limit: PAGE_SIZE };
      if (query) {
        params.q = query;
        params.identityQuery = query;
      }
      if (scope) params.scope = scope;
      if (status !== 'all') params.status = status;
      if (beforeId != null) params.before_id = beforeId;
      return params;
    },
    [query, scope, status]
  );

  const fullLoad = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const page = await getKbEntries(buildParams());
      setRows(page.entries || []);
      setNextBeforeId(page.next_before_id ?? null);
    } catch (err) {
      setError('Failed to load AI knowledge entries');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const loadOlder = useCallback(async () => {
    if (nextBeforeId == null) return;
    try {
      setLoadingMore(true);
      const page = await getKbEntries(buildParams(nextBeforeId));
      setRows((prev) => [...prev, ...(page.entries || [])]);
      setNextBeforeId(page.next_before_id ?? null);
    } catch (err) {
      setError('Failed to load older entries');
      console.error(err);
    } finally {
      setLoadingMore(false);
    }
  }, [buildParams, nextBeforeId]);

  useEffect(() => { fullLoad(); }, [fullLoad]);

  // ---- Learned experience load (non-fatal) ----
  const loadExperience = useCallback(async () => {
    try {
      setExpLoading(true);
      setExpError(null);
      const res = await api.get('/aiinfo/qa-experience');
      setExp(Array.isArray(res.data?.entries) ? res.data.entries : []);
      setExpSoftErrors(Object.values(res.data?.errors || {}) as string[]);
    } catch (err) {
      setExpError('Failed to load learned experience');
      console.error(err);
    } finally {
      setExpLoading(false);
    }
  }, []);

  useEffect(() => { loadExperience(); }, [loadExperience]);

  const toggleExperience = async (r: QaExperienceRow) => {
    const action = r.active ? 'ignore' : 'restore';
    if (action === 'ignore' && !window.confirm('Ignore this learned experience? The AI will stop referencing it. You can restore it later.')) return;
    try {
      setExpBusyId(r.id);
      setExpError(null);
      await api.post('/aiinfo/memory/' + r.id, { action });
      await loadExperience();
    } catch (err) {
      setExpError('Failed to ' + action + ' experience #' + r.id);
      console.error(err);
    } finally {
      setExpBusyId(null);
    }
  };

  // ---- Q&A edit/archive ----
  const openEdit = (entry: QaKbEntry) => {
    setEditing(entry);
    setEditQuestion(entry.question_text || '');
    setEditAnswer(entry.answer_text || '');
    setEditScope(entry.scope === 'customer' ? 'customer' : 'general');
    setEditError(null);
  };

  const closeEdit = () => {
    setEditing(null);
    setEditError(null);
    setSaving(false);
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      setEditError(null);
      await editKbEntry(editing.id, {
        question_text: editQuestion,
        answer_text: editAnswer,
        scope: editScope,
      });
      closeEdit();
      await fullLoad();
    } catch (err) {
      setEditError('Failed to save changes');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const doArchive = async (entry: QaKbEntry) => {
    if (!window.confirm('Archive this entry? The AI will stop using it. You can restore it later.')) return;
    try {
      setBusyId(entry.id);
      setActionError(null);
      await archiveKbEntry(entry.id);
      await fullLoad();
    } catch (err) {
      setActionError('Failed to archive entry #' + entry.id);
      console.error(err);
    } finally {
      setBusyId(null);
    }
  };

  const doRestore = async (entry: QaKbEntry) => {
    try {
      setBusyId(entry.id);
      setActionError(null);
      await editKbEntry(entry.id, { status: 'active' });
      await fullLoad();
    } catch (err) {
      setActionError('Failed to restore entry #' + entry.id);
      console.error(err);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="flex-1 flex flex-col">
      {/* Filter bar — mirrors the v1 sticky bar styling. */}
      <div className="bg-white px-3 sm:px-6 pt-3 sm:pt-4 border-b border-gray-300">
        <div className="flex flex-wrap items-end gap-2 sm:gap-3 pb-3">
          <div className="mr-auto">
            <h2 className="text-lg font-bold text-gray-900">Q&amp;A knowledge</h2>
            <p className="text-xs text-gray-500">All learned Q&amp;A — browse, edit, archive across every customer</p>
          </div>

          <div className="relative flex-shrink-0">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customer, question, or answer…"
              title="Searches customer identity (name / entity id / email) and question/answer text"
              className="w-60 sm:w-72 px-3 py-2 pr-8 text-sm rounded-lg border border-gray-300 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                title="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
              >
                ✕
              </button>
            )}
          </div>

          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            title="Filter by scope"
            className={'flex-shrink-0 px-2 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-brand-500 ' +
              (scope ? 'border-brand-500 text-brand-700 bg-brand-50 font-medium' : 'border-gray-300 text-gray-700 bg-white')}
          >
            {SCOPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            title="Filter by status"
            className={'flex-shrink-0 px-2 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-brand-500 ' +
              (status !== 'active' ? 'border-brand-500 text-brand-700 bg-brand-50 font-medium' : 'border-gray-300 text-gray-700 bg-white')}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <button
            onClick={() => fullLoad()}
            className="flex-shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 rounded-lg transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-auto p-3 sm:p-6 bg-gray-50">
        <div className="w-full max-w-[1800px] mx-auto">
          {actionError && (
            <div className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-center justify-between">
              <span>{actionError}</span>
              <button onClick={() => setActionError(null)} className="text-red-500 hover:text-red-700 ml-3">✕</button>
            </div>
          )}

          {/* ---- Q&A knowledge (qa_knowledge_base browse) ---- */}
          {loading && rows.length === 0 && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-700"></div>
            </div>
          )}

          {error && rows.length === 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && rows.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No AI knowledge entries found
            </div>
          )}

          {rows.length > 0 && (
            <>
              {error && (
                <div className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                    <tr>
                      <th className="text-left font-medium px-3 py-2">Scope</th>
                      <th className="text-left font-medium px-3 py-2">Customer</th>
                      <th className="text-left font-medium px-3 py-2">Question</th>
                      <th className="text-left font-medium px-3 py-2">Answer</th>
                      <th className="text-left font-medium px-3 py-2">Used</th>
                      <th className="text-left font-medium px-3 py-2">Status</th>
                      <th className="text-right font-medium px-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((r) => {
                      const isCustomer = r.scope === 'customer' || r.customer_id != null;
                      const archived = r.status === 'archived';
                      const rowBusy = busyId === r.id;
                      return (
                        <tr key={r.id} className={archived ? 'bg-gray-50 hover:bg-gray-100' : 'hover:bg-gray-50'}>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={'px-2 py-0.5 rounded-full text-xs font-medium ' +
                              (isCustomer ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700')}>
                              {isCustomer ? 'Customer' : 'General'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-700 max-w-[220px]">
                            {r.customer_id == null ? (
                              <span className="text-gray-400">—</span>
                            ) : (
                              <div className="truncate" title={(r.customer_name || '') + (r.customer_entityid ? ' (' + r.customer_entityid + ')' : '')}>
                                <span className="font-medium">{r.customer_name || 'Customer #' + r.customer_id}</span>
                                {r.customer_entityid && (
                                  <span className="text-gray-400 ml-1">({r.customer_entityid})</span>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-800 max-w-[280px]">
                            <div className="truncate" title={r.question_text || undefined}>{r.question_text || '—'}</div>
                          </td>
                          <td className="px-3 py-2 text-gray-600 max-w-[320px]">
                            <div className="truncate" title={r.answer_text || undefined}>{r.answer_text || '—'}</div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                            <span className="text-gray-700 font-medium">{r.reuse_count}</span>
                            <span className="text-gray-400 ml-1" title={r.last_used_at || undefined}>({relativeTime(r.last_used_at)})</span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={'px-2 py-0.5 rounded-full text-xs font-medium ' +
                              (archived ? 'bg-gray-200 text-gray-600' : 'bg-green-100 text-green-700')}>
                              {archived ? 'archived' : 'active'}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-right">
                            <button
                              onClick={() => openEdit(r)}
                              className="px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50 rounded transition-colors"
                            >
                              Edit
                            </button>
                            {archived ? (
                              <button
                                onClick={() => doRestore(r)}
                                disabled={rowBusy}
                                className="px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                              >
                                {rowBusy ? '…' : 'Restore'}
                              </button>
                            ) : (
                              <button
                                onClick={() => doArchive(r)}
                                disabled={rowBusy}
                                className="px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                              >
                                {rowBusy ? '…' : 'Archive'}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {nextBeforeId != null && (
                <div className="flex justify-center py-4">
                  <button
                    onClick={() => loadOlder()}
                    disabled={loadingMore}
                    className="px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {loadingMore ? 'Loading…' : 'Load older'}
                  </button>
                </div>
              )}
            </>
          )}

          {/* ---- LEARNED EXPERIENCE (GET /aiinfo/qa-experience) ---- */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-sm font-bold text-gray-900">Learned experience <span className="font-normal text-gray-500">— wrong → right resolutions mined from resolved threads</span></h3>
              </div>
              <button onClick={() => loadExperience()} className="text-xs font-medium text-brand-700 hover:bg-brand-50 px-2 py-1 rounded">Refresh</button>
            </div>

            {expSoftErrors.length > 0 && (
              <div className="mb-2 bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-800">
                {expSoftErrors.map((e, i) => (<div key={i}>{e}</div>))}
              </div>
            )}
            {expError && (
              <div className="mb-2 bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-700 flex items-center justify-between">
                <span>{expError}</span>
                <button onClick={() => setExpError(null)} className="text-red-500 hover:text-red-700 ml-3">✕</button>
              </div>
            )}

            {expLoading ? (
              <div className="text-sm text-gray-400 py-3">Loading…</div>
            ) : exp.length === 0 ? (
              <div className="text-sm text-gray-500 bg-white border border-gray-200 rounded-lg p-3">No learned experience yet.</div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                    <tr>
                      <th className="text-left font-medium px-3 py-2">Question</th>
                      <th className="text-left font-medium px-3 py-2">Answer / Resolution</th>
                      <th className="text-left font-medium px-3 py-2">Thread</th>
                      <th className="text-left font-medium px-3 py-2">Status</th>
                      <th className="text-right font-medium px-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {exp.map((r) => {
                      const rowBusy = expBusyId === r.id;
                      const active = r.active !== false;
                      return (
                        <tr key={'exp-' + r.id} className={active ? 'hover:bg-gray-50 align-top' : 'bg-gray-50 hover:bg-gray-100 align-top'}>
                          <td className="px-3 py-2 text-gray-800 max-w-[300px]">
                            <div className="truncate" title={r.wrong_summary || undefined}>{r.wrong_summary || '—'}</div>
                          </td>
                          <td className="px-3 py-2 text-gray-600 max-w-[360px]">
                            <div className="truncate" title={r.right_summary || undefined}>{r.right_summary || '—'}</div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                            {r.origin_thread_ref != null && r.origin_thread_ref !== '' ? (
                              <a href={'#/threads/' + r.origin_thread_ref} title={'Thread ' + r.origin_thread_ref}
                                className="text-brand-700 hover:underline">{String(r.origin_thread_ref)}</a>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={'px-2 py-0.5 rounded-full text-xs font-medium ' +
                              (active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600')}>
                              {active ? 'active' : 'ignored'}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-right">
                            {active ? (
                              <button onClick={() => toggleExperience(r)} disabled={rowBusy}
                                className="px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50">
                                {rowBusy ? '…' : 'Ignore memory'}
                              </button>
                            ) : (
                              <button onClick={() => toggleExperience(r)} disabled={rowBusy}
                                className="px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50 rounded transition-colors disabled:opacity-50">
                                {rowBusy ? '…' : 'Restore'}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={closeEdit}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900">Edit knowledge entry #{editing.id}</h3>
              <button onClick={closeEdit} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <div className="px-5 py-4 space-y-4">
              {editError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  {editError}
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Question</label>
                <textarea
                  value={editQuestion}
                  onChange={(e) => setEditQuestion(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Answer</label>
                <textarea
                  value={editAnswer}
                  onChange={(e) => setEditAnswer(e.target.value)}
                  rows={5}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Scope</label>
                <select
                  value={editScope}
                  onChange={(e) => setEditScope(e.target.value)}
                  className="px-2 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  {EDIT_SCOPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={closeEdit}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={saveEdit}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-700 hover:bg-brand-800 rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QaKnowledge;
